import className from "classnames/bind";
import { motion } from "motion/react";
import { forwardRef, useEffect, useRef, useState } from "react";
import { Splide, SplideSlide, SplideTrack } from "react-splide-ts";
import { CarouselPagination, Container, Image } from "@components";
import { InducteeArtifactPaperFragment } from "@/__generated__/graphql";

import styles from "./ArtifactPaper.module.scss";
import { usePageWrapperData } from "@/providers/PageWrapperContext";
const cx = className.bind(styles);

type ArtifactPaperModalProps = {
  startIndex: number;
  images: InducteeArtifactPaperFragment["images"];
  onClose: () => void;
  onSlideChange: (index: number) => void;
};

const ArtifactPaperModal = forwardRef<HTMLDivElement, ArtifactPaperModalProps>(({ startIndex, images, onClose, onSlideChange }, ref) => {
  const modalContentRef = useRef<HTMLDivElement>(null);
  const { labels } = usePageWrapperData();
  const containerRef = useRef<HTMLDivElement>(null);
  const splideRef = useRef<Splide>(null);
  const [activeIndex, setActiveIndex] = useState(startIndex);

  const updateActiveIndex = () => {
    if (splideRef.current) {
      const { index } = splideRef.current.splide;
      setActiveIndex(index);
      onSlideChange(index);
    }
  };

  useEffect(() => {
    document.body.classList.add("locked");
    return () => {
      document.body.classList.remove("locked");
    }
  }, []);

  // Override accessibility attributes
  useEffect(() => {
    if (!modalContentRef.current) return;
    const lists = modalContentRef.current.querySelectorAll('.splide__list');
    lists.forEach(list => {
      list.setAttribute('role', 'list');
    });
    const slides = modalContentRef.current.querySelectorAll('.splide__slide');
    slides.forEach(slide => {
      slide.setAttribute('role', 'listitem');
    });
  }, []);

  return (
    <div ref={ref} className={cx(['artifact-paper-modal', className])}>
      <div className={cx('artifact-paper-modal__content')} ref={modalContentRef}>
        <Splide
          className={cx("splide")}
          ref={splideRef}
          options={{
            type: 'loop',
            arrows: true,
            pagination: true,
            perPage: 1,
            perMove: 1,
            speed: 500,
            start: startIndex,
            updateOnMove: true,
            fixedWidth: '100%',
            // Responsive options (smaller than desktop)
            breakpoints: {
              // Below 1025px
              1025: {
                arrows: false
              },
            }
          }}
          onActive={updateActiveIndex}
          hasTrack={false}
        >
          <SplideTrack>
            {images.map((image, index) => (
              <SplideSlide className={cx('slide')} key={index}>
                <div className={cx('modal-image-bg')} onClick={onClose}></div>
                <div className={cx('modal-image-container')} key={index}>
                  <div className={cx('modal-image')} key={index}>
                    <Image image={image.image} />
                  </div>
                </div>
              </SplideSlide>
            ))}
          </SplideTrack>
          <Container>
            {images.map((image, index) => (
              <p aria-hidden={index!==activeIndex} className={cx('modal-caption', 'text-m')} key={index}>{image.caption}</p>
            ))}
          </Container>
          <Container className={cx('bottom')}>
            <CarouselPagination className={cx('pagination')} ref={containerRef} />
          </Container>
        </Splide>
      </div>
      <button aria-label={labels?.closeLabel} className={cx('close-button')} onClick={onClose}>
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 57 57">
          <path className={cx('circle')} d="M28.5,1.25h0c15.05,0,27.25,12.2,27.25,27.25h0c0,15.05-12.2,27.25-27.25,27.25h0c-15.05,0-27.25-12.2-27.25-27.25h0C1.25,13.45,13.45,1.25,28.5,1.25Z" fill="transparent" stroke="currentColor" strokeWidth="1.5"/>
          <path className={cx('icon')} d="M36.28,22.29l-1.57-1.57-6.21,6.21-6.21-6.21-1.57,1.57,6.21,6.21-6.21,6.21,1.57,1.57,6.21-6.21,6.21,6.21,1.57-1.57-6.21-6.21,6.21-6.21Z" fill="currentColor"/>
        </svg>
      </button>
    </div>
  );
});

export default motion.create(ArtifactPaperModal)